import React, { useEffect, useState, useRef, useMemo, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import { ReactComponent as CriticalIcon } from "../../assets/images/critical-icon.svg";
import CButton from "../../components/CButton";
import CTextField from "../../components/CTextField";
import CDataGrid from "../../components/CDataGrid";
import CDialog from "../../components/CDialog";
import { GridCloseIcon } from "@mui/x-data-grid";
import {
  checkTrainingsCriticalStatus,
} from "../../utils/criticalSkillsTrainingUtils";
import { fetchCriticalSkills } from "../../slices/trainingStrategyInputSlice";
import { addTrainingIsCriticalLog, updateTrainingIsCriticalLog } from "../../slices/trainingTypeSlice";
import { supabase } from "../../config/supabaseClient";
import axios from "axios";
import { formatZAR } from "../../constant/utils/commonUtils";

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Combines skill name and description for better semantic matching
 * Format: "Skill Name.(Description)" - provides more context for embeddings
 */
const combineSkillNameAndDescription = (skill) => {
  const skillName = skill.skill_name?.trim() || "";
  const skillDescription = skill.description?.trim() || "";
  
  if (skillDescription) {
    return `${skillName}.(${skillDescription})`;
  }
  return skillName;
};

/**
 * Extracts training data from API response item
 * Handles both payload structure and direct item structure
 */
const extractTrainingDataFromItem = (item) => {
  if (item.payload) {
    return {
      trainingData: item.payload,
      trainingId: item.id || item.payload.training_id || item.payload.id,
      similarityScore: item.score || item.best_score || null,
      matchedSkills: item.matched_skills || [],
      bestMatchedSkill: item.best_matched_skill || null,
    };
  } else {
    return {
      trainingData: item,
      trainingId: item.training_id || item.id,
      similarityScore: item.score || item.best_score || null,
      matchedSkills: item.matched_skills || [],
      bestMatchedSkill: item.best_matched_skill || null,
    };
  }
};

/**
 * Extracts skill name from combined string (e.g., "Skill Name.(Description)")
 * Returns just the skill name part
 */
const extractSkillNameFromCombined = (matchedSkill) => {
  if (!matchedSkill) return null;
  // If it contains a period, extract the part before it (skill name)
  const periodIndex = matchedSkill.indexOf('.');
  return periodIndex > 0 ? matchedSkill.substring(0, periodIndex).trim() : matchedSkill.trim();
};

/**
 * Finds matching critical skills when API returns matched_skills
 */
const findMatchingCriticalSkills = (matchedSkills, criticalSkills) => {
  return criticalSkills.filter((skill) => {
    const skillName = skill.skill_name;
    // Check if matchedSkills includes the skill name directly
    if (matchedSkills.includes(skillName)) {
      return true;
    }
    // Also check if any matched skill string starts with this skill name (in case backend returns combined string)
    return matchedSkills.some((matched) => 
      matched === skillName || matched.startsWith(`${skillName}.`)
    );
  });
};

/**
 * Finds primary matched skill from API response
 */
const findPrimaryMatchedSkill = (bestMatchedSkill, matchedCriticalSkills, criticalSkills) => {
  if (bestMatchedSkill) {
    const extractedName = extractSkillNameFromCombined(bestMatchedSkill);
    return criticalSkills.find((skill) => 
      skill.skill_name === extractedName || skill.skill_name === bestMatchedSkill
    ) || null;
  }
  return matchedCriticalSkills.length > 0 ? matchedCriticalSkills[0] : null;
};

/**
 * Infers matched skill when API doesn't return matched_skills
 * Uses fallback logic based on training content and available skills
 */
const inferMatchedSkill = (trainingData, criticalSkills) => {
  if (criticalSkills.length === 1) {
    // Only one skill - assign to it
    return {
      primaryMatchedSkill: criticalSkills[0],
      matchedCriticalSkills: [criticalSkills[0]],
    };
  }
  
  if (trainingData.targeted_skills) {
    // Try to match based on targeted_skills
    const targetedSkillsLower = trainingData.targeted_skills.toLowerCase();
    const matched = criticalSkills.find((skill) => {
      const skillNameLower = skill.skill_name?.toLowerCase() || "";
      return targetedSkillsLower.includes(skillNameLower);
    });
    
    if (matched) {
      return {
        primaryMatchedSkill: matched,
        matchedCriticalSkills: [matched],
      };
    }
  }
  
  // Fallback: assign to first skill
  return {
    primaryMatchedSkill: criticalSkills[0],
    matchedCriticalSkills: [criticalSkills[0]],
  };
};

/**
 * Processes and maps API response items to training objects with matched skills
 */
const processTrainingItems = (items, criticalSkills) => {
  return items.map((item) => {
    const {
      trainingData,
      trainingId,
      similarityScore,
      matchedSkills,
      bestMatchedSkill,
    } = extractTrainingDataFromItem(item);

    let matchedCriticalSkills = [];
    let primaryMatchedSkill = null;

    if (matchedSkills.length > 0 || bestMatchedSkill) {
      // API returned matched skills - use them
      matchedCriticalSkills = findMatchingCriticalSkills(matchedSkills, criticalSkills);
      primaryMatchedSkill = findPrimaryMatchedSkill(
        bestMatchedSkill,
        matchedCriticalSkills,
        criticalSkills
      );
    } else {
      // API didn't return matched_skills - infer from training content
      const inferred = inferMatchedSkill(trainingData, criticalSkills);
      primaryMatchedSkill = inferred.primaryMatchedSkill;
      matchedCriticalSkills = inferred.matchedCriticalSkills;
    }

    return {
      ...trainingData,
      id: trainingId,
      training_id: trainingId,
      similarity_score: similarityScore,
      is_critical: trainingData.is_critical !== undefined ? trainingData.is_critical : false,
      matched_critical_skill_id: primaryMatchedSkill?.id || null,
      matched_critical_skill_name: primaryMatchedSkill?.skill_name || null,
      all_matched_skills: matchedCriticalSkills,
      match_score: similarityScore || 0,
    };
  });
};

/**
 * Filters out trainings with invalid data
 */
const filterInvalidTrainings = (trainings) => {
  return trainings.filter((training) => {
    const hasValidName = training.name && training.name.trim().length > 0;
    const hasValidId = training.id || training.training_id;

    if (!hasValidName || !hasValidId) {
      return false;
    }
    return true;
  });
};

/**
 * Filters out trainings that don't match any critical skill
 */
const filterTrainingsWithoutMatchedSkills = (trainings) => {
  return trainings.filter((training) => {
    const hasMatchedSkillName = 
      training.matched_critical_skill_name && 
      training.matched_critical_skill_name.trim().length > 0;
    
    return !!hasMatchedSkillName;
  });
};

/**
 * Sorts trainings by critical skill name, then by similarity score, then by name
 */
const sortTrainings = (trainings) => {
  return [...trainings].sort((a, b) => {
    const skillA = a.matched_critical_skill_name || "";
    const skillB = b.matched_critical_skill_name || "";
    
    if (skillA !== skillB) {
      return skillA.localeCompare(skillB);
    }
    
    const scoreA = a.similarity_score || 0;
    const scoreB = b.similarity_score || 0;
    if (scoreB !== scoreA) {
      return scoreB - scoreA;
    }
    
    return (a.name || "").localeCompare(b.name || "");
  });
};

/**
 * Calls the fetchTrainingProgrammes API
 */
const callFetchTrainingProgrammesAPI = async (skillsWithDescriptions, companyId) => {
  try {
    const response = await axios.post(
      `${process.env.REACT_APP_BACKEND_BASE_URL}/fetchTrainingProgrammes`,
      {
        skillsArray: skillsWithDescriptions,
        company_id: companyId,
        userDesignation: null,
        trainingType: null,
        programDuration: null,
      },
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${process.env.REACT_APP_BACKEND_API_KEY}`,
        },
      }
    );
    return response;
  } catch (axiosError) {
    console.error("Error calling fetchTrainingProgrammes API:", axiosError);
    const errorMessage =
      axiosError.response?.data?.error ||
      axiosError.message ||
      "Failed to fetch training programmes";
    throw new Error(errorMessage);
  }
};

// ============================================================================
// Main Component
// ============================================================================

const AssignCriticalTrainingPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const localUser = JSON.parse(localStorage.getItem("sdfUser"));
  const companyId = localUser?.company_id;

  // ============================================================================
  // State Management
  // ============================================================================

  const [criticalSkills, setCriticalSkills] = useState([]);
  const [relevantTrainings, setRelevantTrainings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [courseDetailsDialog, setCourseDetailsDialog] = useState({
    open: false,
    description: "",
  });

  // ============================================================================
  // Refs for Component Lifecycle Management
  // ============================================================================

  const isMountedRef = useRef(true);
  const findTrainingsCallRef = useRef(false);
  const toastIdsRef = useRef({});

  // ============================================================================
  // Utility Functions
  // ============================================================================

  /**
   * Updates training in vector database (non-critical operation)
   * Fails gracefully if update doesn't succeed
   */
  const updateTrainingInVectorDB = async (trainingId, updates) => {
    try {
      const response = await axios.put(
        `${process.env.REACT_APP_BACKEND_BASE_URL}/importTrainingCatalogue`,
        {
          trainingId: trainingId,
          updates: updates,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${process.env.REACT_APP_BACKEND_API_KEY}`,
          },
        }
      );

      if (!response.data?.success) {
        console.warn("Vector database update failed:", response.data?.message || response.data?.error);
        return { success: false, error: response.data?.message || response.data?.error };
      }

      return response.data;
    } catch (error) {
      // Don't throw - allow the operation to succeed even if vector update fails
      console.warn("Vector database update failed (non-critical):", error.message || error);
      return { success: false, error: error.message || "Vector update failed" };
    }
  };

  // ============================================================================
  // Effects
  // ============================================================================

  /**
   * Fetch critical skills on component mount
   * Uses Redux action to fetch skills for the current company
   */
  useEffect(() => {
    if (companyId) {
      const loadCriticalSkills = async () => {
        try {
          const result = await dispatch(fetchCriticalSkills());
          if (result.payload) {
            setCriticalSkills(result.payload);
          } else if (result.error) {
            if (isMountedRef.current) {
              const toastId = toastIdsRef.current.fetchError || "fetchError";
              toastIdsRef.current.fetchError = toast.error(
                "Error fetching critical skills",
                { toastId }
              );
            }
          }
        } catch (error) {
          console.error("Error loading critical skills:", error);
          if (isMountedRef.current) {
            const toastId = toastIdsRef.current.fetchError || "fetchError";
            toastIdsRef.current.fetchError = toast.error(
              "Error fetching critical skills",
              { toastId }
            );
          }
        }
      };
      loadCriticalSkills();
    }
  }, [companyId, dispatch]);

  /**
   * Cleanup on component unmount
   * Dismisses all toasts and marks component as unmounted
   */
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      toast.dismiss();
    };
  }, []);

  // ============================================================================
  // Main Handler: Find Relevant Trainings
  // ============================================================================

  /**
   * Main function to find relevant trainings for critical skills
   * Requires manual button click - no automatic loading
   */
  const findRelevantTrainings = async () => {
    // Guard: Check if component is still mounted
    if (!isMountedRef.current) {
      return;
    }

    // Guard: Prevent duplicate calls
    if (findTrainingsCallRef.current) {
      return;
    }

    try {
      findTrainingsCallRef.current = true;
      setLoading(true);

      // Validation: Check company ID
      if (!companyId) {
        const toastId = toastIdsRef.current.companyIdError || "companyIdError";
        toastIdsRef.current.companyIdError = toast.error("Company ID not found", { toastId });
        setLoading(false);
        findTrainingsCallRef.current = false;
        return;
      }

      // Validation: Check critical skills exist
      if (!criticalSkills || criticalSkills.length === 0) {
        const toastId = toastIdsRef.current.noSkillsWarning || "noSkillsWarning";
        toastIdsRef.current.noSkillsWarning = toast.warning(
          "No critical skills found. Please add critical skills first.",
          { toastId }
        );
        setLoading(false);
        findTrainingsCallRef.current = false;
        return;
      }

      // Step 1: Prepare skills with descriptions for API call
      const skillsWithDescriptions = criticalSkills
        .map(combineSkillNameAndDescription)
        .filter(Boolean);

      if (skillsWithDescriptions.length === 0) {
        const toastId = toastIdsRef.current.noValidSkillsWarning || "noValidSkillsWarning";
        toastIdsRef.current.noValidSkillsWarning = toast.warning(
          "No valid critical skills found.",
          { toastId }
        );
        setLoading(false);
        findTrainingsCallRef.current = false;
        return;
      }

      // Step 2: Call API to fetch training programmes
      const response = await callFetchTrainingProgrammesAPI(skillsWithDescriptions, companyId);

      // Step 3: Validate API response
      if (
        !response.data?.success ||
        !response.data?.data ||
        !Array.isArray(response.data.data)
      ) {
        console.error("Invalid response from fetchTrainingProgrammes:", response.data);
        setRelevantTrainings([]);
        setLoading(false);
        findTrainingsCallRef.current = false;
        return;
      }

      const data = response.data;

      // Step 4: Process training items and match with critical skills
      let matchedTrainings = processTrainingItems(data.data, criticalSkills);

      // Step 5: Filter invalid trainings
      matchedTrainings = filterInvalidTrainings(matchedTrainings);

      // Step 6: Filter trainings without matched skills
      matchedTrainings = filterTrainingsWithoutMatchedSkills(matchedTrainings);

      // Step 7: Check if trainings are already marked as critical
      matchedTrainings = await checkTrainingsCriticalStatus(matchedTrainings, companyId);

      // Guard: Check if component is still mounted
      if (!isMountedRef.current) {
        return;
      }

      // Step 8: Handle empty results
      if (matchedTrainings.length === 0) {
        const toastId = toastIdsRef.current.noMatchInfo || "noMatchInfo";
        toastIdsRef.current.noMatchInfo = toast.info(
          "No matching trainings found for the critical skills.",
          { toastId }
        );
        setRelevantTrainings([]);
        setLoading(false);
        findTrainingsCallRef.current = false;
        return;
      }

      // Step 9: Sort trainings
      matchedTrainings = sortTrainings(matchedTrainings);


      // Guard: Final mount check
      if (!isMountedRef.current) {
        return;
      }

      // Step 11: Update state and show success message
      setRelevantTrainings(matchedTrainings);
      const toastId = toastIdsRef.current.success || "findTrainingsSuccess";
      toastIdsRef.current.success = toast.success(
        `Found ${matchedTrainings.length} matching training(s) for ${criticalSkills.length} critical skill(s).`,
        { toastId }
      );
    } catch (error) {
      if (!isMountedRef.current) {
        findTrainingsCallRef.current = false;
        return;
      }
      console.error("Error finding relevant trainings:", error);
      const toastId = toastIdsRef.current.error || "findTrainingsError";
      toastIdsRef.current.error = toast.error(
        `Error finding relevant trainings: ${error.message || "Unknown error"}`,
        { toastId }
      );
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
        // Reset flag after a delay to allow for refresh
        setTimeout(() => {
          findTrainingsCallRef.current = false;
        }, 1000);
      }
    }
  };

  // ============================================================================
  // Training Selection Handlers
  // ============================================================================

  // ============================================================================
  // Critical Toggle Handler
  // ============================================================================

  const handleIsCriticalToggle = useCallback(async (trainingId, isCritical) => {
    const training = relevantTrainings.find(
      (t) => (t.id || t.training_id) === trainingId
    );
    
    if (!training) {
      toast.error("Training not found");
      return;
    }

    const previousValue = training.is_critical || false;

    // Optimistic update - update UI immediately
    setRelevantTrainings((prevData) =>
      prevData.map((row) =>
        (row.id || row.training_id) === trainingId
          ? { ...row, is_critical: isCritical }
          : row
      )
    );

    try {
      const isCompanyTraining = training.company_id !== null && training.company_id !== undefined;

      if (isCompanyTraining) {
        // Company catalogue training - update training_catalogue_data
        const { error: updateError } = await supabase
          .from("training_catalogue_data")
          .update({
            is_critical: isCritical,
          })
          .eq("id", trainingId);

        if (updateError) {
          // Revert UI on database error
          setRelevantTrainings((prevData) =>
            prevData.map((row) =>
              (row.id || row.training_id) === trainingId
                ? { ...row, is_critical: previousValue }
                : row
            )
          );
          console.error("Error updating is_critical:", updateError);
          toast.error(`Failed to update: ${updateError.message}`);
          return;
        }

        // Update vector database (non-critical, but log if fails)
        const vectorUpdateResult = await updateTrainingInVectorDB(trainingId, {
          is_critical: isCritical,
        });
        if (!vectorUpdateResult.success) {
          console.warn(
            "Vector database update failed (non-critical):",
            vectorUpdateResult.error
          );
        }

        toast.success("Is Critical status updated successfully");
      } else {
        // iFundi catalogue training - update/create training_catalogue_data_types
        const { data: existing, error: fetchError } = await supabase
          .from("training_catalogue_data_types")
          .select("id")
          .eq("training_id", trainingId)
          .eq("company_id", companyId)
          .single();

        if (fetchError && fetchError.code !== "PGRST116") {
          // Revert UI on database error
          setRelevantTrainings((prevData) =>
            prevData.map((row) =>
              (row.id || row.training_id) === trainingId
                ? { ...row, is_critical: previousValue }
                : row
            )
          );
          console.error("Error fetching existing record:", fetchError);
          toast.error(`Failed to update: ${fetchError.message}`);
          return;
        }

        if (existing && existing.id) {
          await dispatch(
            updateTrainingIsCriticalLog({
              id: existing.id,
              companyId: companyId,
              isCritical: isCritical,
              trainingId: trainingId,
            })
          ).unwrap();
        } else {
          await dispatch(
            addTrainingIsCriticalLog({
              companyId: companyId,
              isCritical: isCritical,
              trainingId: trainingId,
            })
          ).unwrap();
        }

        // Update vector database (non-critical, but log if fails)
        const vectorUpdateResult = await updateTrainingInVectorDB(trainingId, {
          is_critical: isCritical,
        });
        if (!vectorUpdateResult.success) {
          console.warn(
            "Vector database update failed (non-critical):",
            vectorUpdateResult.error
          );
        }

        toast.success("Is Critical status updated successfully");
      }
    } catch (error) {
      // Revert UI on any unexpected error
      setRelevantTrainings((prevData) =>
        prevData.map((row) =>
          (row.id || row.training_id) === trainingId
            ? { ...row, is_critical: previousValue }
            : row
        )
      );
      console.error("Error updating is_critical:", error);
      toast.error(
        `Error updating is_critical: ${
          error.message || "An unexpected error occurred"
        }`
      );
    }
  }, [relevantTrainings, companyId, dispatch, updateTrainingInVectorDB]);

  const handleAddNewTraining = () => {
    navigate("/add-training-catalogue", {
      state: {
        activeTab: "CompanyCatalogue",
        returnToCriticalSkills: true,
      },
    });
  };

  const handleClose = () => {
    navigate("/critical-skills");
  };

  // ============================================================================
  // Search Filtering
  // ============================================================================

  /**
   * Filters trainings based on search term
   * Searches across multiple fields: skill name, training name, provider, description, etc.
   */
  const filteredTrainings = useMemo(() => {
    if (!searchTerm.trim()) {
      return relevantTrainings;
    }
    
    const searchLower = searchTerm.toLowerCase();
    return relevantTrainings.filter((training) => {
      return (
        training.matched_critical_skill_name?.toLowerCase().includes(searchLower) ||
        training.name?.toLowerCase().includes(searchLower) ||
        training.provider?.toLowerCase().includes(searchLower) ||
        training.description?.toLowerCase().includes(searchLower) ||
        training.targeted_skills?.toLowerCase().includes(searchLower) ||
        training.nqf_level?.toLowerCase().includes(searchLower) ||
        (training.nqf_aligned ? "yes" : "no").includes(searchLower)
      );
    });
  }, [relevantTrainings, searchTerm]);

  // ============================================================================
  // Columns Definition for CDataGrid
  // ============================================================================

  const columns = useMemo(() => [
    {
      field: "matched_critical_skill_name",
      headerName: "Critical Skill Name",
      flex: 1,
      minWidth: 180,
    },
    {
      field: "name",
      headerName: "Training Name",
      flex: 1.5,
      minWidth: 200,
    },
    {
      field: "provider",
      headerName: "Provider",
      flex: 1,
      minWidth: 150,
      renderCell: (params) => params.value || "-",
    },
    {
      field: "cost",
      headerName: "Cost per Learner",
      flex: 1,
      minWidth: 150,
      renderCell: (params) => formatZAR(params.value),
    },
    {
      field: "description",
      headerName: "Training Details",
      flex: 2,
      minWidth: 250,
      renderCell: (params) => {
        const description = params.value || "";
        const truncatedDescription =
          description.length > 100
            ? description.substring(0, 100) + "..."
            : description;

        const handleClick = () => {
          if (description) {
            setCourseDetailsDialog({
              open: true,
              description: description,
            });
          }
        };

        return (
          <span
            className={
              description ? "cursor-pointer hover:text-blue-500 " : ""
            }
            onClick={handleClick}
            title="Click to view full description"
          >
            {truncatedDescription || "-"}
          </span>
        );
      },
    },
    {
      field: "targeted_skills",
      headerName: "Targeted Skills",
      flex: 1.5,
      minWidth: 180,
      renderCell: (params) => params.value || "-",
    },
    {
      field: "nqf_aligned",
      headerName: "NQF Aligned",
      flex: 0.8,
      minWidth: 120,
      renderCell: (params) => params.value ? "Yes" : "No",
    },
    {
      field: "nqf_level",
      headerName: "NQF Level",
      flex: 0.8,
      minWidth: 120,
      renderCell: (params) => params.value || "-",
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 120,
      sortable: false,
      renderCell: (params) => {
        const isCritical = params.row.is_critical || false;
        const iconColor = isCritical ? "#FF383C" : "#008CD1";
        const trainingId = params.row.id || params.row.training_id;
        const uniqueId = `critical-icon-${trainingId}-${isCritical}`;

        return (
          <div className="flex items-center h-full">
            <CButton
              tooltip={isCritical ? "Mark as Not Critical" : "Mark as Critical"}
              tooltipPosition={"top"}
              onClick={() => handleIsCriticalToggle(trainingId, !isCritical)}
              className="!min-w-0 bg-transparent hover:bg-gray-100 2xl:!h-[28px] 2xl:!w-[28px] !h-[24px] !w-[24px] !p-0 !rounded-[5px] inline-flex items-center justify-center"
            >
              <div
                id={uniqueId}
                className="2xl:!w-[24px] 2xl:!h-[24px] !w-[20px] !h-[20px]"
              >
                <CriticalIcon className="w-full h-full" />
                <style
                  dangerouslySetInnerHTML={{
                    __html: `#${uniqueId} svg path { stroke: ${iconColor} !important; }`,
                  }}
                />
              </div>
            </CButton>
          </div>
        );
      },
    },
  ], [handleIsCriticalToggle]);

  // Prepare rows data with proper IDs
  const rowsWithIds = useMemo(() => {
    if (!relevantTrainings || relevantTrainings.length === 0) return [];
    return relevantTrainings
      .filter((training) => training && (training.id || training.training_id))
      .map((training) => ({
        ...training,
        id: training.id || training.training_id,
      }));
  }, [relevantTrainings]);


  return (
    <div className="h-[calc(100vh-116px)] relative flex flex-col">
      <div className="flex items-center justify-between mb-4 flex-shrink-0">
        <h2 className="text-3xl leading-3xl text-blue-500 font-semibold">
          Mark Training Programmes as Critical
        </h2>
        
      </div>
      
      {/* Instructions Section */}
      <div className="border-2 border-blue-500 bg-white rounded-lg p-4 md:mb-6 mb-4 relative flex-shrink-0">
        <p className="bg-white text-lg font-semibold text-gray-700 mb-4 2xl:text-xl 2xl:leading-xl lg:text-base lg:leading-base text-sm leading-sm absolute md:top-[-16px] top-[-12px] left-[16px] px-1">
          Instructions
        </p>
        <div>
          <p className="font-medium mb-0 2xl:text-base text-sm 2xl:leading-base leading-sm text-gray-300">
            Find and assign training programs that match your critical skills. 
            Click "Find Trainings" to automatically search for relevant trainings based on your critical skills, 
            or click "Add New Training" to manually add a new training program to your catalogue. 
            Once trainings are found, select the ones you want to mark as critical and click "Save".
          </p>
        </div>
      </div>

      <div className="bg-white border border-solid border-blue-200 rounded-xl overflow-hidden flex flex-col flex-1">
        {loading ? (
          <div className="w-full h-full flex items-center justify-center">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
              <p className="text-lg text-gray-700 font-medium">Loading trainings...</p>
            </div>
          </div>
        ) : (
          <>
            {/* Fixed Controls Section */}
            {relevantTrainings.length > 0 && (
              <div className="flex items-center justify-between p-4 border-b border-blue-200 gap-4 flex-shrink-0">
                <div className="flex-1 max-w-md">
                  <CTextField
                    placeholder="Search trainings..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full"
                    startIcon={<SearchIcon className="text-blue-300" />}
                  />
                </div>
                <div className="flex items-center gap-3">
                  <CButton
                    onClick={findRelevantTrainings}
                    disabled={loading}
                    className="text-white bg-blue-500 hover:bg-blue-700 border border-solid border-blue-500 hover:border-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    startIcon={<SearchIcon />}
                  >
                    {loading ? "Finding..." : "Refresh Trainings"}
                  </CButton>
                  <CButton
                    onClick={handleAddNewTraining}
                    className="text-white bg-blue-500 hover:bg-blue-700 border border-solid border-blue-500 hover:border-blue-700"
                    startIcon={<AddIcon />}
                  >
                    Add New Training
                  </CButton>
                </div>
              </div>
            )}

            {/* Scrollable Table Area */}
            <div className="flex-1 flex flex-col min-h-0">
              {relevantTrainings.length === 0 && !loading ? (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center">
                    <p className="text-gray-500 mb-4">
                      {criticalSkills.length === 0
                        ? "No critical skills found. Please add critical skills first."
                        : "Click 'Match Trainings' to search for trainings matching your critical skills."}
                    </p>
                    {criticalSkills.length > 0 && (
                      <div className="flex items-center justify-center gap-3">
                        <CButton
                          onClick={findRelevantTrainings}
                          disabled={loading}
                          className="text-white bg-blue-500 hover:bg-blue-700 border border-solid border-blue-500 hover:border-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                          startIcon={<SearchIcon />}
                        >
                          Match Trainings
                        </CButton>
                        <CButton
                          onClick={handleAddNewTraining}
                          className="text-white bg-blue-500 hover:bg-blue-700 border border-solid border-blue-500 hover:border-blue-700"
                          startIcon={<AddIcon />}
                        >
                          Add New Training
                        </CButton>
                      </div>
                    )}
                  </div>
                </div>
              ) : rowsWithIds.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-gray-500 mb-4">
                    No trainings available.
                  </p>
                </div>
              ) : (
                <div className="flex-1 w-full flex flex-col min-h-0">
                  <CDataGrid
                    idKey="id"
                    searchQuery={searchTerm}
                    rows={rowsWithIds}
                    columns={columns}
                    loading={loading}
                    initialPageSize={10}
                    pageSize={10}
                    height="100%"
                  />
                </div>
              )}
            </div>

          </>
        )}
      </div>

      {/* Course Details Dialog */}
      <CDialog
        open={courseDetailsDialog.open}
        onClose={() => setCourseDetailsDialog({ open: false, description: "" })}
        className="course-details-dialog"
        maxWidth="lg"
      >
        <div className="md:w-[900px] w-full flex flex-col max-h-[90vh]">
          {/* Header - Centered */}
          <div className="flex items-center justify-center p-6 pb-4">
            <h2 className="text-xl font-semibold text-gray-800">
              Training Details
            </h2>
          </div>

          {/* Content - Description shown directly */}
          <div className="flex-1 overflow-y-auto px-6 pb-6">
            <div className="text-base text-gray-800 leading-relaxed whitespace-pre-wrap">
              {courseDetailsDialog.description || "No description available."}
            </div>
          </div>

          {/* Footer - Only Close button */}
          <div className="flex justify-center p-6 pt-4">
            <CButton
              onClick={() => setCourseDetailsDialog({ open: false, description: "" })}
              className="bg-blue-500 hover:bg-blue-700 min-w-[100px] text-white border border-solid border-blue-500 hover:border-blue-700"
            >
              Close
            </CButton>
          </div>
        </div>
      </CDialog>
    </div>
  );
};

export default AssignCriticalTrainingPage;

